import './SpendAndEffectBarChart.scss';
import cn from 'classnames';
import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis
} from 'recharts';
import { capitalizeWords } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { ClampedText } from '@Components/ClampedText';
import { DcDgBadge } from '@Components/DcDgBadge/DcDgBadge';
import { Placement } from '@Components/designConstants';
import { InteractiveDiv } from '@Components/InteractiveEl';

import {
  DEFAULT_SORT_OPTIONS,
  DEMAND_GEN_VS_CAP,
  SINGLE_COLOR_SORT_OPTIONS
} from '@Components/SpendAndEffectModal/spend-and-effect-modal-constants';
import { getBarChartContributorsData } from '@Pages/MMM/mmm-utils';
import IncrementalityIcon from '../../../assets/IncrementalityIcon.svg?react';

// `data` shapes here are deeply dynamic MMM payloads; `any` is retained intentionally.
interface SpendAndEffectBarChartProps {
  barData: any[];
  data: any[];
  setBarData?: Dispatch<SetStateAction<any>>;
  displayActualValues: boolean;
  valueFormatter: (value: number) => string;
  activeChannel: string;
  setActiveChannel?: Dispatch<SetStateAction<string>>;
  className?: string;
  colorMap: Record<string, string>;
  expandedModal: boolean | undefined;
  barSize: number;
  setXAxisScale?: Dispatch<SetStateAction<number>>;
  xAxisScale?: number;
  groupBy: string;
  // Per-bar row height in px. Default 70 preserves prior behavior; consumers may
  // raise this to fill a taller container.
  rowHeight?: number;
}

const genToolTipText = (perc: number, chan: string, type: string | boolean): string => {
  let from: string;
  if (type === 'direct') {
    from = 'Direct';
  } else if (type) {
    from = 'Induced by';
  } else {
    from = 'Attributed to';
  }
  return `${perc.toFixed(1)}% ${from} ${chan}`;
};

// Hoisted so recharts doesn't see a new component type each render; recharts clones
// this element and injects label/payload, which we ignore.
const BarChartTooltip = ({ text }: { text?: string }): ReactElement => (
  <div className='bar-chart-tooltip'>{text}</div>
);

const BAR_MAP = [
  {
    dataKey: 'value',
    stackId: 'spendModal',
    opacity: 1
  },
  {
    dataKey: 'value2',
    stackId: 'revenueModal',
    opacity: 1
  },
  {
    dataKey: 'twoStageSpend',
    stackId: 'spendModal',
    opacity: 1
  },
  {
    dataKey: 'twoStageEffect',
    stackId: 'revenueModal',
    opacity: 1
  }
];

export const SpendAndEffectBarChart = ({
  barData,
  setBarData,
  displayActualValues,
  valueFormatter,
  setActiveChannel,
  activeChannel,
  data,
  className = '',
  colorMap,
  expandedModal = false,
  barSize,
  setXAxisScale,
  xAxisScale,
  groupBy,
  rowHeight = 70
}: SpendAndEffectBarChartProps): ReactElement => {
  const [tooltipText, setTooltipText] = useState<string>('');

  // Two-stage modal renders two of these charts sharing one `xAxisScale` so their bars align.
  // The axis `domain` callback (below) runs during render, so it latches the observed max into a
  // ref rather than calling `setXAxisScale` mid-render (which React 19 flags). The effect flushes
  // the latched max to the shared state after commit; the returned domain value is unchanged, so
  // the rendered axis is identical — only the state write moves out of the render phase.
  const observedMaxRef = useRef(0);
  useEffect(() => {
    if (setXAxisScale && observedMaxRef.current > (xAxisScale ?? 0)) {
      setXAxisScale(observedMaxRef.current);
    }
  });

  const dynamicBars = useMemo(() => {
    const makeCellClick = (item: any) =>
      expandedModal && setBarData && setActiveChannel && activeChannel !== DEMAND_GEN_VS_CAP
        ? () => {
            setBarData(getBarChartContributorsData(data, item.contributors));
            setActiveChannel(item.name);
          }
        : undefined;

    // The inner color is identical for the added (negative) and non-added stripe
    // patterns; only the `Neg` prefix differs.
    const resolveSubBarColor = (itemName: string, channelName: string): string => {
      if (SINGLE_COLOR_SORT_OPTIONS.includes(activeChannel) || channelName === activeChannel) {
        return colorMap[itemName];
      }
      if (itemName === activeChannel || activeChannel === DEMAND_GEN_VS_CAP) {
        return colorMap[channelName];
      }
      return 'grey';
    };

    return BAR_MAP.flatMap((el, i) => {
      if (el.dataKey !== 'twoStageSpend' && el.dataKey !== 'twoStageEffect') {
        return (
          <Bar
            key={`modalBar${i}`}
            barSize={barSize}
            dataKey={el.dataKey}
            isAnimationActive={false}
            opacity={el.opacity}
            stackId={el.stackId}
            yAxisId={0}
          >
            {barData.map(item => (
              <Cell
                key={`modalcell${item.name}${i}`}
                fill={
                  DEFAULT_SORT_OPTIONS.includes(activeChannel) || activeChannel === item.name
                    ? colorMap[item.name]
                    : 'grey'
                }
                onClick={makeCellClick(item)}
                onMouseOver={
                  expandedModal
                    ? () => setTooltipText(genToolTipText(item[el.dataKey], item.name, 'direct'))
                    : undefined
                }
                stroke='white'
                strokeWidth={expandedModal ? 2 : 0}
              />
            ))}
          </Bar>
        );
      }

      const type = el.dataKey === 'twoStageSpend' ? 'spend' : 'effect';
      return data.map((channel, j) => (
        <Bar
          key={`subModalBar${type + j}`}
          barSize={barSize}
          dataKey={`${channel.name}:${type}`}
          isAnimationActive={false}
          opacity={el.opacity}
          stackId={el.stackId}
          yAxisId={0}
        >
          {barData.map(item => (
            <Cell
              key={`subModalCell${item.name}${i}`}
              fill={`url(#shadedStripe${item.added ? 'Neg' : ''}${resolveSubBarColor(
                item.name,
                channel.name
              )})`}
              stroke='white'
              strokeWidth={expandedModal ? 2 : 0}
              onClick={makeCellClick(item)}
              onMouseOver={
                expandedModal
                  ? () =>
                      setTooltipText(
                        genToolTipText(item[`${channel.name}:${type}`], channel.name, item.added)
                      )
                  : undefined
              }
            />
          ))}
        </Bar>
      ));
    });
  }, [
    activeChannel,
    barData,
    barSize,
    colorMap,
    data,
    expandedModal,
    setActiveChannel,
    setBarData
  ]);

  const outlineBarProperties = useMemo(
    () =>
      expandedModal
        ? {
            barSize: barSize - 2,
            // No palette token matches this one-off dark outline used only in the expanded modal.
            stroke: '#4e4e50',
            strokeWidth: 2,
            getFill: () => 'transparent',
            opacity: 1
          }
        : {
            barSize,
            stroke: 'transparent',
            strokeWidth: 0,
            getFill: (name: string) => colorMap[name],
            opacity: 0.5
          },
    [barSize, colorMap, expandedModal]
  );

  return (
    <div className={cn('spend-and-effect-bar-chart', className)}>
      <SpendAndEffectLegend
        barData={barData}
        displayActualValues={displayActualValues}
        expandedModal={expandedModal}
        onClick={(contributors, name) =>
          expandedModal && setActiveChannel && setBarData
            ? () => {
                setBarData(getBarChartContributorsData(data, contributors));
                setActiveChannel(name);
              }
            : undefined
        }
        valueFormatter={valueFormatter}
        groupBy={groupBy}
      />
      <ResponsiveContainer
        className='rechart-container'
        width='100%'
        height={barData.length * rowHeight}
      >
        <BarChart layout='vertical' barCategoryGap={6} barGap={8} data={barData}>
          <XAxis
            type='number'
            domain={
              expandedModal && setXAxisScale
                ? [
                    0,
                    (dataMax: number) => {
                      if (!xAxisScale || dataMax > xAxisScale) {
                        observedMaxRef.current = Math.max(observedMaxRef.current, dataMax);
                        return dataMax;
                      }
                      return xAxisScale;
                    }
                  ]
                : [0, 'auto']
            }
            hide
          />
          <YAxis type='category' dataKey='value' yAxisId={0} hide />
          <YAxis type='category' dataKey='value' yAxisId={1} hide />
          {expandedModal && (
            <RechartsTooltip
              shared={false}
              content={<BarChartTooltip text={tooltipText} />}
              cursor={{ fill: 'transparent' }}
              wrapperStyle={{ zIndex: 20 }}
            />
          )}
          {expandedModal && dynamicBars}
          <Bar
            key='spendOutlineBar'
            barSize={outlineBarProperties.barSize}
            // Collapsed view has no stacked sub-bars, so its outline is the whole bar and stays
            // net; expanded view stacks sub-bars on top (dynamicBars, below) that sum to gross
            // regardless of `added` (DEV-24230 -- see the *BarValue fields in DataFormatters.tsx).
            dataKey={expandedModal ? 'totalActualizedSpendBarValue' : 'totalActualizedSpend'}
            isAnimationActive={false}
            opacity={1}
            stroke={outlineBarProperties.stroke}
            strokeWidth={outlineBarProperties.strokeWidth}
            radius={2}
            yAxisId={1}
            style={{ pointerEvents: 'none' }}
          >
            {barData.map(({ name }) => (
              <Cell key={name} fill={outlineBarProperties.getFill(name)} />
            ))}
          </Bar>
          <Bar
            key='effectOutlineBar'
            barSize={outlineBarProperties.barSize}
            dataKey={expandedModal ? 'totalActualizedEffectBarValue' : 'totalActualizedEffect'}
            isAnimationActive={false}
            opacity={outlineBarProperties.opacity}
            stroke={outlineBarProperties.stroke}
            strokeWidth={outlineBarProperties.strokeWidth}
            radius={2}
            yAxisId={1}
            style={{ pointerEvents: 'none' }}
          >
            {barData.map(({ name }) => (
              <Cell key={name} fill={outlineBarProperties.getFill(name)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

interface SpendAndEffectLegendProps {
  barData: any[];
  displayActualValues: boolean;
  expandedModal: boolean;
  onClick?: (contributors: any, name: string) => void;
  valueFormatter: (value: number) => string;
  groupBy?: string;
}

const SpendAndEffectLegend = ({
  barData,
  displayActualValues,
  expandedModal,
  onClick,
  valueFormatter,
  groupBy = 'name'
}: SpendAndEffectLegendProps): ReactElement => (
  <div
    className={cn('chart-legend', {
      'hide-actual-values': !displayActualValues,
      'show-actual-values': displayActualValues,
      expanded: expandedModal,
      collapsed: !expandedModal
    })}
  >
    {barData.map((item, index) => (
      <LegendItem
        key={item.name}
        item={item}
        prevChannelName={index > 0 ? barData[index - 1].channelName : undefined}
        expandedModal={expandedModal}
        displayActualValues={displayActualValues}
        valueFormatter={valueFormatter}
        groupBy={groupBy}
        onClick={onClick}
      />
    ))}
  </div>
);

const IncrementalityMark = (): ReactElement => (
  <>
    {' '}
    <IncrementalityIcon className='incrementality-icon-inline' />
  </>
);

interface LegendItemProps {
  item: any;
  prevChannelName?: string;
  expandedModal: boolean;
  displayActualValues: boolean;
  valueFormatter: (value: number) => string;
  groupBy: string;
  onClick?: (contributors: any, name: string) => void;
}

const LegendItem = ({
  item,
  prevChannelName,
  expandedModal,
  displayActualValues,
  valueFormatter,
  groupBy,
  onClick
}: LegendItemProps): ReactElement => {
  const { name, displayName, channelName, incrementality, contributors, added } = item;
  const isFirstOfChannel = Boolean(channelName) && channelName !== prevChannelName;

  return (
    <InteractiveDiv
      className='legend-item'
      onClick={onClick ? () => onClick(contributors, name) : undefined}
    >
      {expandedModal && (
        <>
          <LegendItemExpandedLabel
            item={item}
            isFirstOfChannel={isFirstOfChannel}
            groupBy={groupBy}
          />
          {groupBy !== 'channel' && channelName && (
            <LegendItemPlatformTacticBrand item={item} groupBy={groupBy} />
          )}
        </>
      )}
      {!expandedModal && (
        <>
          <div className='item-icon'>
            <DcDgBadge isDemandCapture={!added} />
          </div>
          <ClampedText className='name-text' fullText={displayName}>
            <span>
              {displayName}
              {incrementality && <IncrementalityMark />}
            </span>
          </ClampedText>
        </>
      )}
      <LegendItemValues
        item={item}
        expandedModal={expandedModal}
        displayActualValues={displayActualValues}
        valueFormatter={valueFormatter}
      />
    </InteractiveDiv>
  );
};

interface LegendItemExpandedLabelProps {
  item: any;
  isFirstOfChannel: boolean;
  groupBy: string;
}

const LegendItemExpandedLabel = ({
  item,
  isFirstOfChannel,
  groupBy
}: LegendItemExpandedLabelProps): ReactElement => {
  const { channelName, displayName, incrementality, added } = item;

  if (!channelName) {
    return (
      <ClampedText className='name-text' fullText={displayName}>
        <span>
          {displayName}
          {incrementality && <IncrementalityMark />}
        </span>
      </ClampedText>
    );
  }

  if (!isFirstOfChannel) {
    return <div className='channel-text-spacer' />;
  }

  return (
    <>
      <div className='item-icon'>
        <OverlayTrigger
          placement={Placement.TOP}
          overlay={
            <Tooltip id='dgDcIconTooltip'>
              {added ? 'Demand Generation ' : 'Demand Capture '}
              {/* TODO: format groupBy to pretty display name */}
              {groupBy}
            </Tooltip>
          }
        >
          <DcDgBadge isDemandCapture={!added} />
        </OverlayTrigger>
      </div>
      <ClampedText className='channel-text' fullText={capitalizeWords(channelName)}>
        <span>
          {capitalizeWords(channelName)}
          {incrementality && groupBy === 'channel' && <IncrementalityMark />}
        </span>
      </ClampedText>
    </>
  );
};

interface LegendItemPlatformTacticBrandProps {
  item: any;
  groupBy: string;
}

const LegendItemPlatformTacticBrand = ({
  item,
  groupBy
}: LegendItemPlatformTacticBrandProps): ReactElement => {
  const { platformName, tacticName, brandNonBrandName, incrementality } = item;

  return (
    <div className='platform-tactic-brand-text'>
      <ClampedText className='platform-label' fullText={capitalizeWords(platformName)}>
        <span>
          {capitalizeWords(platformName)}
          {incrementality && ['platform', 'name'].includes(groupBy) && <IncrementalityMark />}
        </span>
      </ClampedText>
      {groupBy === 'name' && (
        <ClampedText
          className='tactic-brand-label'
          fullText={capitalizeWords(`${tacticName}, ${brandNonBrandName}`)}
        >
          {capitalizeWords(`${tacticName}, ${brandNonBrandName}`)}
        </ClampedText>
      )}
    </div>
  );
};

interface LegendItemValuesProps {
  item: any;
  expandedModal: boolean;
  displayActualValues: boolean;
  valueFormatter: (value: number) => string;
}

const LegendItemValues = ({
  item,
  expandedModal,
  displayActualValues,
  valueFormatter
}: LegendItemValuesProps): ReactElement => {
  const {
    totalActualizedSpend,
    totalActualizedEffect,
    totalActualizedSpendRawValueLabel,
    totalActualizedEffectRawValueLabel,
    totalEffectForChannelLabel,
    totalSpendForChannelLabel,
    contributorsSpendSum,
    contributorsEffectSum,
    added
  } = item;
  const directSpend = added
    ? totalActualizedSpend - contributorsSpendSum
    : totalActualizedSpend + contributorsSpendSum;
  const directEffect = added
    ? totalActualizedEffect - contributorsEffectSum
    : totalActualizedEffect + contributorsEffectSum;

  return (
    <div className='item-values-container'>
      <div className='item-value spend'>
        <div className='value-label'>Spend:</div>
        <div className='actualized-value'>{valueFormatter(totalActualizedSpend)}</div>
        {displayActualValues && (
          <div className='actual-value'>{totalActualizedSpendRawValueLabel}</div>
        )}
        {expandedModal && (
          <>
            <div className='vertical-divider' />
            <div className='direct-value'>{valueFormatter(directSpend)}</div>
          </>
        )}
        {displayActualValues && <div className='actual-value'>{totalSpendForChannelLabel}</div>}
      </div>
      <div className='item-value effect'>
        <div className='value-label'>Effect:</div>
        <div className='actualized-value'>{valueFormatter(totalActualizedEffect)}</div>
        {displayActualValues && (
          <div className='actual-value'>{totalActualizedEffectRawValueLabel}</div>
        )}
        {expandedModal && (
          <>
            <div className='vertical-divider' />
            <div className='direct-value'>{valueFormatter(directEffect)}</div>
          </>
        )}
        {displayActualValues && <div className='actual-value'>{totalEffectForChannelLabel}</div>}
      </div>
    </div>
  );
};
